import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  ChevronDownIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { DangerButton, SecondaryButton, SmallDangerButton } from '@shared/components/buttons';
import { Modal } from '@shared/components/modal';
import { Tooltip } from '@shared/components/popover/tooltip';
import { classNames } from '@shared/helpers/class-names';
import { shortCurrencyFormatter } from '@shared/helpers/format-currency';
import { Link } from '@tanstack/react-router';
import { format } from 'date-fns/format';
import { useCallback, useMemo, useState } from 'react';
import { EmptyState } from '../../components/empty-state';
import { SmallSecondaryLinkButton } from '../../components/link-buttons';
import { StatusCell } from '../../components/status/status-cell';
import type { Order, OrderDeliveryState } from '../../data/order';
import { orderDeliveryState } from '../../data/order';
import { StoreOperation, unavailableReason } from '../../data/permissions';
import type { Store } from '../../data/store';
import type { Vendor } from '../../data/vendor';
import { useDeleteOrder, useMaybeCreateOrder, useOrders } from '../../hooks/orders';
import { useVendors } from '../../hooks/vendors';
import { storeLayout } from '../../route-apis';

function NewOrderMenu({
  vendors,
  createOrder,
}: {
  vendors: Vendor[];
  createOrder: (vendor: Vendor) => Promise<void>;
}) {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <MenuButton className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-primary-600">
          New Order
          <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
        </MenuButton>
      </div>

      <MenuItems
        transition
        className="absolute right-0 z-popover mt-2 w-56 origin-top-right rounded-md bg-popover shadow-popover focus:outline-hidden transition ease-out duration-100 data-closed:scale-95 data-closed:opacity-0 data-leave:duration-75 data-leave:ease-in"
      >
        <div className="py-1">
          {vendors.map((vendor) => (
            <MenuItem key={vendor.vendor_id}>
              {({ focus }) => (
                <button
                  onClick={() => createOrder(vendor)}
                  className={classNames(
                    focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                    'block w-full text-left px-4 py-2 text-sm'
                  )}
                  type="button"
                >
                  {vendor.name}
                </button>
              )}
            </MenuItem>
          ))}
        </div>
      </MenuItems>
    </Menu>
  );
}

const VendorName = ({ id, vendors }: { id: string; vendors: Vendor[] }) => {
  const vendor = useMemo(() => {
    return vendors.find((v) => v.vendor_id === id);
  }, [id, vendors]);

  return vendor?.name;
};

function DeliveryIndicator({ state }: { state: OrderDeliveryState }) {
  if (state === 'clean') {
    return null;
  }

  // 'failed' = every rep bounced (red); 'partial'/'unknown' = warn (amber).
  const failed = state === 'failed';

  return (
    <Tooltip
      content={
        failed
          ? 'This order could not be delivered to the vendor. Every address bounced.'
          : "Some recipients didn't receive this order."
      }
    >
      <StatusCell variant={failed ? 'danger' : 'attention'} className="gap-1">
        {failed ? (
          <ExclamationCircleIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        ) : (
          <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        )}
        {failed ? 'Send failed' : 'Sending issue'}
      </StatusCell>
    </Tooltip>
  );
}

// Everything that differs between Open Orders and History, in one place. The
// two lists used to be told apart by a `route: '/edit' | '/summary'` prop that
// each level then re-derived something from — the row's link target, the
// action button's label, the list's empty-state title — so the same
// `route === '/edit'` branch was written three times at two altitudes. The
// parent already knows which list it is rendering; it now says so once, by
// name, and a third list would be a compile error here rather than a silent
// fall-through to History's behaviour.
const ORDER_LISTS = {
  open: {
    to: '/stores/$storeId/orders/$orderId/edit',
    actionLabel: 'Edit',
    emptyTitle: 'No open orders',
  },
  history: {
    to: '/stores/$storeId/orders/$orderId/summary',
    actionLabel: 'View',
    emptyTitle: 'No order history yet',
  },
} as const;

type OrderListKind = keyof typeof ORDER_LISTS;

function OrderCard({
  store,
  vendors,
  order,
  kind,
  onCancel,
  cancelUnavailableReason,
}: {
  store: Store;
  vendors: Vendor[];
  order: Order;
  kind: OrderListKind;
  onCancel: (order: Order) => void;
  cancelUnavailableReason: string | null;
}) {
  // The server decides row membership: the recompute + expand pass
  // (services/order_ops.go) writes a draft's stored `items`, so they already
  // are the membership, and `total_*` — computed off the unscrubbed order in
  // newOrderResponse — is the number the send path would ship.
  const totalCases = order.total_cases;
  const totalUnits = order.total_units;
  const totalCost = order.total_dollars;
  const deliveryState = orderDeliveryState(order);

  // Gated on the order's status, not on which list rendered it: `OrderList` is
  // shared between Open Orders and History, and the status is the real
  // precondition. Cancelling a SENT order is something the API would allow —
  // `DeleteOrder` has no draft gate — but it cannot recall an email or an FTP
  // drop the vendor already has, so offering it would tell a store owner they
  // stopped something they did not.
  const cancellable = order.status === 'draft';
  const { to, actionLabel } = ORDER_LISTS[kind];
  // One object for both links in the row — they are the same destination, and
  // two literals would be two things to keep in step.
  const orderParams = { storeId: store.id, orderId: order.id };

  return (
    // The cancel control is a SIBLING of the link, not a descendant: a <button>
    // inside an <a> is invalid markup, and its click bubbles into the
    // navigation whatever the handler does. It lives in a trailing action
    // column, separated by a 1px rule — gray-200, one step up from the list's
    // own gray-100 row rules, because at gray-100 the column boundary vanished
    // against white — rather than floating in the gutter next to the totals: a
    // destructive action wedged against the row's other affordances is one
    // slipped click from cancelling the wrong order, and this audience is
    // mouse-first on cheap hardware.
    //
    // The row's own affordance is a named button in that column, not a
    // trailing chevron. A chevron next to an outlined Cancel is two different
    // control vocabularies a few pixels apart, and the chevron is the weaker
    // one: it says "there is more" where the row can now say what happens.
    // The whole row stays clickable behind it.
    //
    // Every row gets the column, cancellable or not, at a fixed width: it is
    // what keeps an Open Orders row's totals on the same vertical as a History
    // row's. The two lists sat ~100px out of register while only drafts
    // reserved the space.
    <li className="flex items-stretch hover:bg-gray-50">
      <Link
        to={to}
        params={orderParams}
        className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-6 gap-y-4 p-5 sm:flex-nowrap"
      >
        <div>
          <div className="flex items-center gap-x-2">
            <p className="text-sm font-semibold leading-6 text-gray-900">
              <VendorName id={order.vendor_id} vendors={vendors} />
            </p>
            <DeliveryIndicator state={deliveryState} />
          </div>
          <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
            <p>Last Updated {format(order.updated_at, 'MM/dd/yyyy hh:mmaa')}</p>
          </div>
        </div>
        <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1 w-full">
            <span className="w-16 text-right">{totalCases} cases</span>
            <span className="w-16 text-right">{totalUnits} units</span>
            <span className="w-20 text-right font-medium">{shortCurrencyFormatter(totalCost)}</span>
          </div>
          <p className="text-sm leading-6 text-gray-900">{order.status}</p>
        </div>
      </Link>
      <div className="flex w-32 shrink-0 flex-col justify-center gap-2 border-l border-gray-200 px-5">
        <SmallSecondaryLinkButton to={to} params={orderParams}>
          {actionLabel}
        </SmallSecondaryLinkButton>
        {/* w-full, not the column's own stretch: with a `disabledReason` the
            button ships inside the tooltip's focusable span, and it is the
            span that becomes the flex item. */}
        {cancellable && (
          <SmallDangerButton
            className="w-full"
            onClick={() => onCancel(order)}
            disabledReason={cancelUnavailableReason}
          >
            Cancel
          </SmallDangerButton>
        )}
      </div>
    </li>
  );
}

function OrderList({
  store,
  vendors,
  orders,
  kind,
  onCancel,
  cancelUnavailableReason,
}: {
  store: Store;
  vendors: Vendor[];
  orders: Order[];
  kind: OrderListKind;
  onCancel: (order: Order) => void;
  cancelUnavailableReason: string | null;
}) {
  return (
    <ul className="divide-y divide-gray-100">
      {orders.map((order) => (
        <OrderCard
          key={order.id}
          store={store}
          vendors={vendors}
          order={order}
          kind={kind}
          onCancel={onCancel}
          cancelUnavailableReason={cancelUnavailableReason}
        />
      ))}
      {orders.length === 0 && (
        <EmptyState
          icon={<ClipboardDocumentListIcon className="size-8" />}
          title={ORDER_LISTS[kind].emptyTitle}
        />
      )}
    </ul>
  );
}

export function Orders() {
  const { store } = storeLayout.useRouteContext();
  const { orders } = useOrders();
  const { vendors } = useVendors();
  const enabledVendors = useMemo(() => {
    return vendors.filter((v) => !v.disabled);
  }, [vendors]);
  const maybeCreateOrder = useMaybeCreateOrder();

  // Cancelling is owner/manager only on the API side, so an employee gets the
  // button disabled with the reason rather than a 403 they cannot act on.
  const cancelUnavailableReason = unavailableReason(store, StoreOperation.CancelOrder);
  const deleteOrder = useDeleteOrder();

  // One modal for the whole list, holding the order it would cancel. A <Modal>
  // per card would mount a Headless UI Dialog and its Portal for every draft
  // on the page.
  const [orderPendingCancel, setOrderPendingCancel] = useState<Order | null>(null);
  const dismissCancel = useCallback(() => setOrderPendingCancel(null), []);
  const confirmCancel = useCallback(() => {
    if (orderPendingCancel) {
      // useDeleteOrder drops the order from the list optimistically and owns
      // its own error alert, so there is nothing to await here.
      deleteOrder(orderPendingCancel);
    }

    setOrderPendingCancel(null);
  }, [orderPendingCancel, deleteOrder]);

  return (
    <div className="h-full w-full flex flex-col">
      <div className="max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-3xl font-semibold text-gray-900">Open Orders</h1>
          <NewOrderMenu
            vendors={enabledVendors}
            createOrder={(vendor) => maybeCreateOrder(store, vendor)}
          />
        </div>
        <OrderList
          store={store}
          vendors={vendors}
          orders={orders.filter((order) => order.status === 'draft')}
          kind="open"
          onCancel={setOrderPendingCancel}
          cancelUnavailableReason={cancelUnavailableReason}
        />
        <h1 className="text-3xl font-semibold text-gray-900 px-4 py-3">History</h1>
        <OrderList
          store={store}
          vendors={vendors}
          orders={orders.filter((order) => order.status !== 'draft')}
          kind="history"
          onCancel={setOrderPendingCancel}
          cancelUnavailableReason={cancelUnavailableReason}
        />
      </div>
      {/* Same copy and the same two buttons as the order editor's confirm
          (pages/orders/edit/index.tsx) — two places asking "are you sure you
          want to cancel this order?" in different words would be worse than
          the missing button was. Driven by `visible`, never by a mount guard,
          so Headless UI can run the leave transition. */}
      <Modal
        title="Cancel Order"
        visible={orderPendingCancel !== null}
        onClose={dismissCancel}
        size="sm"
        footer={
          <>
            <SecondaryButton onClick={dismissCancel}>Keep Order</SecondaryButton>
            <DangerButton onClick={confirmCancel}>Cancel Order</DangerButton>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to cancel this order? This cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
